import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { SegmentListMultipleItemMembershipOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { CustomerTagsSegment } from "@redotech/redo-conversion-core-common/post-purchase/post-purchase-flow";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { OperatorSelector } from "../../operator-selector";

interface CustomerTagsSegmentCommonProps {
  condition: CustomerTagsSegment;
}

export type CustomerTagsSegmentProps = CustomerTagsSegmentCommonProps &
  (
    | {
        readonly: false;
        setCondition: (condition: CustomerTagsSegment) => void;
        searchCustomerTags: (params: {
          search: string;
        }) => Promise<{ tags: string[]; total: number }>;
      }
    | { readonly: true }
  );

const operatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "at least one tag",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "none of the tags",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "all tags",
};

const toBadgeListItem = (tag: string): BadgeRedoListItem<string> => ({
  id: tag,
  value: tag,
  type: "badge",
  badge: { text: tag, color: "gray" },
});

export const CustomerTagsSegmentCondition = memo(
  function CustomerTagsSegmentCondition({
    condition,
    ...props
  }: CustomerTagsSegmentProps) {
    const handleOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, operator });
        }
      },
    );

    return (
      <>
        <Text fontSize="xs" textColor="primary">
          Customer has
        </Text>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleOperatorSelected}
          operatorToText={operatorToText}
          readonly={props.readonly}
          selectedOperator={condition.operator}
        />
        <Text fontSize="xs" textColor="primary">
          in the list:
        </Text>
        {props.readonly ? (
          <Flex dir="row" gap="md" w="full" wrap="wrap">
            {condition.tags.length === 0 ? (
              <Text fontSize="xs" textColor="secondary">
                No tags selected
              </Text>
            ) : (
              condition.tags.map((tag, index) => (
                <RedoBadge color="gray" key={index} size="sm" text={tag} />
              ))
            )}
          </Flex>
        ) : (
          <CustomerTagsInput
            onTagsSelected={(tags) =>
              props.setCondition({ ...condition, tags })
            }
            searchCustomerTags={props.searchCustomerTags}
            selectedTags={condition.tags}
          />
        )}
      </>
    );
  },
);

const CustomerTagsInput = memo(function CustomerTagsInput({
  selectedTags,
  onTagsSelected,
  searchCustomerTags,
}: {
  selectedTags: string[];
  onTagsSelected: (tags: string[]) => void;
  searchCustomerTags: (params: {
    search: string;
  }) => Promise<{ tags: string[]; total: number }>;
}) {
  const [searchString, setSearchString] = useState("");
  const debouncedSearchString = useDebounce(searchString, 500);

  const tagsLoad = useLoad(async () => {
    try {
      return await searchCustomerTags({ search: debouncedSearchString });
    } catch (error) {
      console.error("Error searching customer tags:", error);
      return { tags: [], total: 0 };
    }
  }, [debouncedSearchString, searchCustomerTags]);

  const tagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    const allTags = new Set([...(tagsLoad.value?.tags ?? []), ...selectedTags]);
    return Array.from(allTags).map(toBadgeListItem);
  }, [tagsLoad.value, selectedTags]);

  const selectedTagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return selectedTags.map(toBadgeListItem);
  }, [selectedTags]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      onTagsSelected(items.map((item) => item.value));
    },
  );

  return (
    <Flex w="full">
      <RedoTagsDropdownInput<string>
        description={
          selectedTags.length > 0
            ? `${selectedTags.length} tag${selectedTags.length !== 1 ? "s" : ""} selected`
            : undefined
        }
        loading={tagsLoad.pending}
        options={tagOptions}
        placeholder="Enter or select tags"
        searchPlaceholder="Search for customer tags..."
        searchString={searchString}
        selectedOptions={selectedTagOptions}
        setSearchString={setSearchString}
        setSelectedOptions={handleSelectionChange}
        size="xs"
        totalOptionsIncludingNotShown={tagsLoad.value?.total}
      />
    </Flex>
  );
});
